import React from 'react'
import { Card } from 'antd'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import CommonLayout from '../../../../../layouts/Common'
import Chart from 'react-apexcharts';
import { Container, HRVHeader, StyledCard } from './style'
import { StyledButton } from '../../../../../components/Common/Button/style'
import { formatDateWithoutTime } from '../../../../../helpers'

type Props = {}

type LegendType = "top" | "right" | "bottom" | "left" | undefined;

const HRVResult = (props: Props) => {
  const { hrvId } = useParams();
  const location = useLocation();
  const [payload, setPayload] = React.useState(location.state?.payload);
  const [client] = useSelector((state: any) => [state.professionals.selectedClient]);

  React.useEffect(() => {
    if (!payload && hrvId) {
      const stored = sessionStorage.getItem(`analysis_payload_${hrvId}`);
      if (stored) {
        setPayload(JSON.parse(stored));
      }
    }
  }, [hrvId, payload]);
  const navigate = useNavigate();
  const [isViewerVisible, setIsViewerVisible] = React.useState(true);
  const [isEnlarged, setIsEnlarged] = React.useState(false);
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [isBlobLoading, setIsBlobLoading] = React.useState(false);

  React.useEffect(() => {
    if (payload?.attachmentUrl) {
      setIsBlobLoading(true);
      fetch(payload.attachmentUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
          setIsBlobLoading(false);
        })
        .catch(err => {
          console.error('Failed to proxy PDF blob:', err);
          setIsBlobLoading(false);
        });
    }
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [payload?.attachmentUrl]);


  const stressAndRecoverySeries = [
    {
      name: 'Day 1',
      type: 'column',
      data: [100, 50, 100, 23, 50, 100, 23, 54, 23, 62, 35]
    },
    {
      name: 'Day 2',
      type: 'column',
      data: [54, 76, 24, 27]
    },
    {
      name: 'Good balance',
      type: 'line',
      data: [23, 42, 35, 27, 43, 22, 17, 31, 22, 22, 12, 16],
    },
  ];

  const stressAndRecoveryOptions = {
    chart: {
      id: 'line',
    },
    legend: {
      position: 'top' as LegendType,
      offsetY: -20,
    },
    stroke: {
      width: [0, 5],
    },
    title: {
      text: 'Stress and Recovery Profile'
    },
    xaxis: {
      categories: [
        'Self-reported sleep quality',
        'Amount of stress reactions (% >60%)',
        'Energy Expenditure (% kcal)',
        'Time of recovery Day / Night (% >30%)',
        'Length of sleep (% > 7 hours)',
        'Amount of recovery during sleep (% > 75%)',
        'Age based Quality of Good recovery (% ms)',
        'RMSSD night / Age Based Minimum RMSSD',
        'Biocoherence Recovery Index (%)'
      ]
    },
    yaxis: {
      title: {
        text: 'Relative amount (%)',
      },
      min: 0,
      max: 100,
      tickAmount: 10
    }
  };

  const rmssdSeries = [
    {
      name: 'RMSSD awake time (ms)',
      type: 'column',
      data: [54, 76]
    },
    {
      name: 'RMSSD during sleep (ms)',
      type: 'column',
      data: [23, 42],
    },
    {
      name: 'Age Based minimum RMSSD during sleep (ms)',
      type: 'column',
      data: [23, 42],
    },
    {
      name: 'Age Based minimal Good Recovery RMSSD (ms)',
      type: 'column',
      data: [23, 42],
    },
    {
      name: 'Relative RMSSD difference (%)',
      type: 'line',
      data: [100, 0]
    },
  ];

  const rmssdOptions = {
    chart: {
      id: 'line',
    },
    legend: {
      position: 'top' as LegendType,
      offsetY: 0,
    },
    stroke: {
      width: [0, 5],
    },
    markers: {
      size: 5
    },
    title: {
      text: 'RMSSD Profile'
    },
    xaxis: {
      categories: [
        'Day 1',
        'Day 2',
      ]
    },
    yaxis: {
      title: {
        text: 'RMSSD (ms)',
      },
      min: 0,
      max: 100,
      tickAmount: 10
    }
  };

  return (
    <CommonLayout title="HRV Results" width="100%">
      <div style={{ width: '100%', padding: '0 20px' }}>
        <HRVHeader>
          <Card style={{ width: '100%' }}>
            <div className="hrv-details">
              <div>
                <p>Report number:</p>
                <p>{payload?.reportNumber}</p>
              </div>
              <div>
                <p>Date of analysis:</p>
                <p>{payload?.dateOfAnalysis && formatDateWithoutTime(payload.dateOfAnalysis)}</p>
              </div>
              <div>
                <p>Client</p>
                <p>{payload?.client ? payload.client : `${client?.firstName} ${client?.familyName}`}</p>
              </div>
              <div>
                <p>Client ID</p>
                <p>{payload?.clientGid || client?.gid}</p>
              </div>
            </div>
          </Card>
        </HRVHeader>

        {payload?.attachmentUrl && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '1rem', justifyContent: 'flex-start' }}>
              <StyledButton 
                onClick={() => setIsViewerVisible(!isViewerVisible)}
                style={{ background: isViewerVisible ? '#E2E8F0' : '#0066D2', color: isViewerVisible ? '#4A5568' : '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                {isViewerVisible ? 'Hide Attachment' : 'Show Attachment'}
              </StyledButton>
              <StyledButton 
                onClick={() => {
                  const downloadUrl = blobUrl || payload.attachmentUrl;
                  const link = document.createElement('a');
                  link.href = downloadUrl;
                  link.download = `HRV_Report_${payload.reportNumber || payload.id}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ background: '#718096', color: '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                Download PDF
              </StyledButton>
              <StyledButton 
                onClick={() => window.open(payload.attachmentUrl, '_blank')}
                style={{ background: '#0066D2', color: '#fff', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
              >
                Open in New Tab
              </StyledButton>
              {isViewerVisible && (
                <StyledButton 
                  onClick={() => setIsEnlarged(!isEnlarged)}
                  style={{ background: '#E2E8F0', color: '#4A5568', border: 'none', height: '36px', padding: '0 16px', borderRadius: '6px' }}
                >
                  {isEnlarged ? 'Original Size' : 'Enlarge View'}
                </StyledButton>
              )}
            </div>

            {isViewerVisible && (
              <Card>
                <p style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '1.5rem' }}>Lifestyle Assessment</p>
                <div style={{ position: 'relative', width: '100%', height: isEnlarged ? '1200px' : '650px', border: '1px solid #edf2f7', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc', transition: 'height 0.3s ease' }}>
                  {isBlobLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <p>Loading report viewer...</p>
                    </div>
                  ) : (
                    <iframe
                      src={blobUrl ? `${blobUrl}#toolbar=0` : `${payload.attachmentUrl}#toolbar=0`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="HRV Report"
                    />
                  )}
                </div>
              </Card>
            )}
          </div>
        )}

        <StyledCard>
          <Container>
            <Chart options={stressAndRecoveryOptions} series={stressAndRecoverySeries} type="line" height={350} />
            <Chart options={rmssdOptions} series={rmssdSeries} type="line" height={350} />
          </Container>
        </StyledCard>
      </div>
    </CommonLayout>
  )
}

export default HRVResult;